// file: apps/admin/src/pages/lessons/index.jsx
import { useState } from 'react';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import CostIcon from '@mui/icons-material/PriceChange';
import IosShareIcon from '@mui/icons-material/IosShare';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PathDataGrid } from '~common/components';
import PageTitle from '../../components/pageTitle';
import useStyles from './styles';
import columns from './columns';
import useLessons from './hooks/useLessons';
import EditSessionDialog from './editLessonDialog';
import EditCostDialog from './editCostDialog';
import ExportLessonsDialog from './exportLessonsDialog';

const isProductionFrontend = import.meta.env.VITE_SENTRY_ENV === 'production';

function Lessons() {
  const { classes } = useStyles();
  const [showDateFilterPanel, setShowDateFilterPanel] = useState(true);

  // default to 2 months ago until end of today
  const [startDate, setStartDate] = useState(dayjs().subtract(2, 'month').startOf('day'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));

  const {
    data,
    alert,
    loading,
    selectedSession,
    selectionModel,
    showExportDialog,
    onSelectionModelChange,
    showEditSessionDialog,
    openEditSessionDialog,
    dismissEditSessionDialog,
    submitEditSessionDialog,
    showEditCostDialog,
    openEditCostDialog,
    dismissEditCostDialog,
    submitEditCostDialog,
    openExportDialog,
    dismissExportDialog,
    tutor,
    commitChanges,
    handleEditCost,
    handleCompleteSession,
    onRefreshClick,
  } = useLessons({
    startDate: startDate.valueOf(),
    endDate: endDate.valueOf(),
  });

  const toolbarActions = [
    {
      id: 'edit-details',
      label: 'Edit Details',
      startIcon: <EditIcon />,
      disabled: selectedSession === null || loading,
      onClick: openEditSessionDialog,
    },
    {
      id: 'edit-costs',
      label: 'Edit Costs',
      startIcon: <CostIcon />,
      disabled: selectedSession === null || loading,
      onClick: openEditCostDialog,
    },
    ...(!isProductionFrontend ? [{
      id: 'complete-session',
      label: 'Complete Session',
      startIcon: <CheckCircleIcon />,
      disabled: selectedSession === null || loading || selectedSession?.status !== 'ACCEPTED',
      onClick: handleCompleteSession,
    }] : []),
    {
      id: 'export',
      label: 'Export',
      startIcon: <IosShareIcon />,
      disabled: loading,
      onClick: openExportDialog,
    },
  ];

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <PageTitle title="Lessons" />
      </div>
      <div className={classes.content}>
        <Grid container className={classes.grid}>
          <Grid item xs={12} className={classes.gridItem}>
            <Paper className={classes.background}>
              {alert && (
                <Alert severity={alert.severity} className={classes.alert}>
                  {alert.message}
                </Alert>
              )}
              {showDateFilterPanel && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box className={classes.dateFilterPanel}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(newVal) => newVal && setStartDate(newVal.startOf('day'))}
                      renderInput={(params) => <TextField {...params} size="small" margin="dense" />}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(newVal) => newVal && setEndDate(newVal.endOf('day'))}
                      renderInput={(params) => <TextField {...params} size="small" margin="dense" />}
                    />
                    <Button variant="outlined" size="medium" onClick={onRefreshClick} disabled={loading}>
                      Apply
                    </Button>
                  </Box>
                </LocalizationProvider>
              )}
              <PathDataGrid
                className={classes.dataGrid}
                initialSorting={[{ id: 'startDate', desc: true }]}
                loading={loading}
                rows={data}
                columns={columns}
                getRowId={(row) => row.id}
                pageSize={20}
                onRefresh={onRefreshClick}
                onSelectionModelChange={onSelectionModelChange}
                selectionModel={selectionModel}
                toolbarActions={toolbarActions}
                toolbarIconButtons={[
                  {
                    id: 'date-filter',
                    icon: <CalendarMonthIcon />,
                    title: 'Date filter',
                    active: showDateFilterPanel,
                    onClick: () => setShowDateFilterPanel((prev) => !prev),
                  },
                ]}
                showColumnFilters
                showColumnManager
                searchPlaceholder="Search..."
                checkboxSelection={false}
              />
            </Paper>
          </Grid>
        </Grid>
      </div>

      {showEditSessionDialog && (
        <EditSessionDialog
          id={selectedSession?.id}
          open={showEditSessionDialog}
          onClose={dismissEditSessionDialog}
          onCancel={dismissEditSessionDialog}
          onSubmit={submitEditSessionDialog}
          selectedSession={selectedSession}
          tutor={tutor}
          commitChanges={commitChanges}
          setAppointmentFormVisible={() => dismissEditSessionDialog()}
        />
      )}

      {showEditCostDialog && (
        <EditCostDialog
          id={selectedSession?.id}
          open={showEditCostDialog}
          onClose={dismissEditCostDialog}
          onCancel={dismissEditCostDialog}
          onSubmit={submitEditCostDialog}
          selectedSession={selectedSession}
          tutor={tutor}
          commitChanges={handleEditCost}
          setAppointmentFormVisible={() => dismissEditCostDialog()}
        />
      )}

      {showExportDialog && (
        <ExportLessonsDialog open={showExportDialog} onClose={dismissExportDialog} data={data} />
      )}
    </div>
  );
}

export default Lessons;
